import { useEffect, useState } from "react";
import { api, fmtEur } from "../lib/api";
import { useApp } from "../lib/store";
import {
  LINE_TYPES, LINE_TYPE_LABELS, PRODUCT_FAMILIES, PRODUCT_FAMILY_LABELS, LINE_UNITS,
  FAMILY_PRICING, FAMILY_DEFAULT_UNIT, FAMILY_SPECS, LINE_TYPE_PRICING,
  newLineItem, normalizeLine, lineExtended, lineCost,
} from "../lib/fields";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Plus, Trash2, Copy, ChevronDown, Package, Factory, Truck } from "lucide-react";

function marginPct(l) {
  const ext = lineExtended(l);
  return ext ? Math.round(((ext - lineCost(l)) / ext) * 1000) / 10 : 0;
}

export function LineItemsEditor({ lines, setLines, canWrite, currency = "EUR" }) {
  const { t, isManager, picklists } = useApp();
  const [plants, setPlants] = useState([]);
  const [openSpec, setOpenSpec] = useState({});

  useEffect(() => {
    api.get("/plants").then((r) => setPlants(r.data || [])).catch(() => setPlants([]));
  }, []);

  const setLine = (i, patch) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const changeType = (i, lineType) => {
    const patch = { line_type: lineType, pricing_mode: LINE_TYPE_PRICING[lineType] || "flat" };
    if (lineType === "Product") {
      patch.product_family = lines[i].product_family || "Roll/Pressure-Sensitive Labels";
      patch.pricing_mode = FAMILY_PRICING[patch.product_family];
      patch.unit = lines[i].unit || FAMILY_DEFAULT_UNIT[patch.product_family];
    } else {
      patch.unit = "lot";
    }
    setLine(i, patch);
  };

  const changeFamily = (i, fam) => setLine(i, {
    product_family: fam, pricing_mode: FAMILY_PRICING[fam], unit: FAMILY_DEFAULT_UNIT[fam],
  });

  const addLine = (lineType) => setLines((ls) => [...ls, newLineItem(lineType)]);
  const duplicateLine = (i) => setLines((ls) => {
    const copy = { ...ls[i] };
    const next = [...ls];
    next.splice(i + 1, 0, copy);
    return next;
  });
  const removeLine = (i) => setLines((ls) => ls.filter((_, j) => j !== i));

  const grandTotal = lines.reduce((s, l) => s + lineExtended(l), 0);
  const grandCost = lines.reduce((s, l) => s + lineCost(l), 0);
  const grandMargin = grandTotal ? Math.round(((grandTotal - grandCost) / grandTotal) * 1000) / 10 : 0;

  const subcontractors = picklists.subcontractors || [];

  return (
    <div data-testid="line-items-editor">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("line_items")}</h2>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Select value="" onValueChange={(v) => addLine(v)}>
              <SelectTrigger className="h-9 w-52" data-testid="add-line-type-select">
                <div className="flex items-center gap-1.5 text-sm"><Plus className="h-3.5 w-3.5" />{t("add_line")}</div>
              </SelectTrigger>
              <SelectContent>
                {LINE_TYPES.map((lt) => <SelectItem key={lt} value={lt}>{t(LINE_TYPE_LABELS[lt])}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {lines.length === 0 && (
        <div className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg p-6 text-center" data-testid="no-line-items">
          {t("no_line_items")}
        </div>
      )}

      <div className="space-y-3">
        {lines.map((l, i) => {
          const isProduct = l.line_type === "Product";
          const specs = isProduct ? (FAMILY_SPECS[l.product_family] || FAMILY_SPECS.Other) : [];
          const ext = lineExtended(l);
          const specOpen = openSpec[i] ?? (isProduct && i === 0);
          return (
            <div key={i} className="border border-gray-200 rounded-lg p-4 bg-white" data-testid={`line-item-${i}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[180px]">
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("line_type")}</label>
                  <Select value={l.line_type} onValueChange={(v) => changeType(i, v)} disabled={!canWrite}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`line-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{LINE_TYPES.map((lt) => <SelectItem key={lt} value={lt}>{t(LINE_TYPE_LABELS[lt])}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {isProduct && (
                  <div className="flex-1 min-w-[180px]">
                    <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("product_family")}</label>
                    <Select value={l.product_family} onValueChange={(v) => changeFamily(i, v)} disabled={!canWrite}>
                      <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`product-family-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PRODUCT_FAMILIES.map((f) => <SelectItem key={f} value={f}>{t(PRODUCT_FAMILY_LABELS[f])}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="flex items-center gap-1 pt-4">
                  {canWrite && (
                    <button className="text-gray-300 hover:text-[#1B2A4A] transition-colors" title={t("duplicate")}
                      data-testid={`duplicate-line-${i}`} onClick={() => duplicateLine(i)}><Copy className="h-4 w-4" /></button>
                  )}
                  {canWrite && (
                    <button className="text-gray-300 hover:text-red-500 transition-colors" title={t("delete")}
                      data-testid={`delete-line-${i}`} onClick={() => removeLine(i)}><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </div>

              <div className="mt-2">
                <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("description")}</label>
                <Input className="h-8 text-xs mt-0.5" disabled={!canWrite} data-testid={`line-desc-${i}`}
                  value={l.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3 items-end">
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("quantity")}</label>
                  <Input className="h-8 text-xs font-mono mt-0.5" type="number" disabled={!canWrite} data-testid={`line-qty-${i}`}
                    value={l.quantity ?? ""} onChange={(e) => setLine(i, { quantity: Number(e.target.value) })} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("unit")}</label>
                  <Select value={l.unit || "pcs"} onValueChange={(v) => setLine(i, { unit: v })} disabled={!canWrite}>
                    <SelectTrigger className="h-8 text-xs mt-0.5" data-testid={`line-unit-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{LINE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">
                    {t("unit_price")}{l.pricing_mode === "per_1000" ? " /1000" : l.pricing_mode === "flat" ? ` (${t("pm_flat")})` : ""}
                  </label>
                  <Input className="h-8 text-xs font-mono mt-0.5" type="number" step="0.01" disabled={!canWrite} data-testid={`line-price-${i}`}
                    value={l.unit_price ?? ""} onChange={(e) => setLine(i, { unit_price: Number(e.target.value) })} />
                </div>
                {isManager && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("cost")}</label>
                    <Input className="h-8 text-xs font-mono mt-0.5" type="number" step="0.01" disabled={!canWrite} data-testid={`line-cost-${i}`}
                      value={l.cost ?? ""} onChange={(e) => setLine(i, { cost: Number(e.target.value) })} />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("extended_price")}</label>
                  <div className="h-8 flex items-center font-mono font-semibold text-sm text-[#1B2A4A]" data-testid={`line-ext-${i}`}>{fmtEur(ext, currency)}</div>
                </div>
                {isManager && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t("margin")}</label>
                    <div className="h-8 flex items-center font-mono text-sm text-emerald-600" data-testid={`line-margin-${i}`}>{marginPct(l)}%</div>
                  </div>
                )}
              </div>

              {/* Sourcing */}
              <div className="mt-3 border-t border-gray-100 pt-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold flex items-center gap-1"><Factory className="h-3 w-3" />{t("sourcing")}</span>
                  <div className="flex rounded-md border border-gray-200 overflow-hidden">
                    {["Internal", "External"].map((s) => (
                      <button key={s} disabled={!canWrite} data-testid={`sourcing-${s.toLowerCase()}-${i}`}
                        onClick={() => setLine(i, { sourcing_type: s })}
                        className={`px-3 py-1 text-xs transition-colors ${l.sourcing_type === s ? "bg-[#1B2A4A] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                        {s === "Internal" ? t("sourcing_internal") : t("sourcing_external")}
                      </button>
                    ))}
                  </div>
                  {l.sourcing_type === "Internal" ? (
                    <Select value={l.sourcing_plant_id || ""} disabled={!canWrite}
                      onValueChange={(v) => { const p = plants.find((x) => x.id === v); setLine(i, { sourcing_plant_id: v, sourcing_plant_name: p?.name || "" }); }}>
                      <SelectTrigger className="h-8 w-48 text-xs" data-testid={`plant-select-${i}`}><SelectValue placeholder={t("select_plant")} /></SelectTrigger>
                      <SelectContent>{plants.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <>
                      <Input className="h-8 w-48 text-xs" list={`subs-${i}`} disabled={!canWrite} placeholder={t("subcontractor")} data-testid={`subcontractor-${i}`}
                        value={l.subcontractor || ""} onChange={(e) => setLine(i, { subcontractor: e.target.value })} />
                      <datalist id={`subs-${i}`}>{subcontractors.map((s) => <option key={s} value={s} />)}</datalist>
                      {isManager && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-gray-400">{t("subcontractor_cost")}</span>
                          <Input className="h-8 w-24 text-xs font-mono" type="number" step="0.01" disabled={!canWrite} data-testid={`subcontractor-cost-${i}`}
                            value={l.subcontractor_cost ?? ""} onChange={(e) => setLine(i, { subcontractor_cost: Number(e.target.value) })} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Spec panel (Product only) */}
              {isProduct && (
                <div className="mt-3 border-t border-gray-100 pt-2">
                  <button className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#1B2A4A] transition-colors"
                    data-testid={`spec-toggle-${i}`} onClick={() => setOpenSpec((o) => ({ ...o, [i]: !specOpen }))}>
                    <Package className="h-3.5 w-3.5" />{t("specifications")}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform ${specOpen ? "" : "-rotate-90"}`} />
                  </button>
                  {specOpen && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid={`spec-panel-${i}`}>
                      {specs.map((sp) => (
                        <div key={sp.key} className={sp.type === "textarea" ? "col-span-2 md:col-span-4" : ""}>
                          <label className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{t(sp.label)}</label>
                          {sp.type === "textarea" ? (
                            <Textarea className="text-xs mt-0.5" rows={2} disabled={!canWrite} data-testid={`spec-${sp.key}-${i}`}
                              value={l[sp.key] || ""} onChange={(e) => setLine(i, { [sp.key]: e.target.value })} />
                          ) : (
                            <Input className="h-8 text-xs mt-0.5" type={sp.type === "number" ? "number" : "text"} disabled={!canWrite}
                              data-testid={`spec-${sp.key}-${i}`} value={l[sp.key] ?? ""}
                              onChange={(e) => setLine(i, { [sp.key]: sp.type === "number" ? Number(e.target.value) : e.target.value })} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-2">
                <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 text-[10px] flex items-center gap-1 w-fit" data-testid={`line-sourcing-badge-${i}`}>
                  {l.sourcing_type === "Internal" ? <><Factory className="h-2.5 w-2.5" />{l.sourcing_plant_name || t("sourcing_internal")}</> : <><Truck className="h-2.5 w-2.5" />{l.subcontractor || t("sourcing_external")}</>}
                </Badge>
              </div>
            </div>
          );
        })}
      </div>

      {lines.length > 0 && (
        <div className="flex items-center justify-end gap-6 mt-4 pt-3 border-t border-gray-200">
          {isManager && <span className="text-sm text-emerald-600 font-mono" data-testid="line-items-margin">{t("margin")}: {grandMargin}%</span>}
          <span className="text-sm text-gray-500">{t("total")}: <span className="text-xl font-bold font-mono text-[#1B2A4A]" data-testid="line-items-total">{fmtEur(grandTotal, currency)}</span></span>
        </div>
      )}
    </div>
  );
}

export { normalizeLine };
